import { defineStore } from 'pinia'
import axios from 'axios'

// The platform console is a SaaS-operator surface, entirely separate from any
// tenant. It carries its own scope=platform token (never a tenant token), so it
// uses a dedicated axios instance rather than the shared tenant client — no JWT
// collision, and a 401 here must NOT bounce to the tenant /login.
const protocol =
  process.env.NODE_ENV === 'production' ? 'https:' : window.location.protocol
const baseURL =
  process.env.VUE_APP_API_URL ||
  `${protocol}//${window.location.host}${process.env.VUE_APP_API_PATH || '/api'}`

// "Remember me" decides where the session lives: localStorage survives a browser
// restart, sessionStorage is wiped when the tab closes. Reads check both so either
// choice works; writes/clears touch both to avoid a stale copy in the other bucket.
function readToken() {
  return localStorage.getItem('platform_token') || sessionStorage.getItem('platform_token')
}
function readAdmin() {
  return localStorage.getItem('platform_admin') || sessionStorage.getItem('platform_admin')
}
function storeSession(token, admin, remember) {
  const store = remember ? localStorage : sessionStorage
  const other = remember ? sessionStorage : localStorage
  store.setItem('platform_token', token)
  store.setItem('platform_admin', JSON.stringify(admin))
  other.removeItem('platform_token')
  other.removeItem('platform_admin')
}
function clearSession() {
  localStorage.removeItem('platform_token')
  localStorage.removeItem('platform_admin')
  sessionStorage.removeItem('platform_token')
  sessionStorage.removeItem('platform_admin')
}

const api = axios.create({ baseURL })
api.interceptors.request.use((config) => {
  const token = readToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// On 401 the platform token is missing/expired — clear it and bounce to the
// platform login. We redirect with window.location (not the router) both to
// avoid importing the router here and to hard-reset Pinia state. Skip the login
// call itself so a bad-credentials 401 stays on the form with its inline error.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const url = error.config?.url || ''
    if (status === 401 && !url.includes('/platform/login')) {
      clearSession()
      if (window.location.pathname !== '/platform/login') {
        window.location.assign('/platform/login')
      }
    }
    return Promise.reject(error)
  },
)

export const usePlatformStore = defineStore('platform', {
  state: () => ({
    token: readToken() || null,
    admin: JSON.parse(readAdmin() || 'null'),
    unreadMessages: 0,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isOwner: (state) => state.admin?.role === 'platform_owner',
  },

  actions: {
    async login(credentials, remember = false) {
      const { data } = await api.post('/platform/login', credentials)
      this.token = data.token
      this.admin = data.admin
      storeSession(data.token, data.admin, remember)
      return data.admin
    },

    async forgotPassword(email) {
      const { data } = await api.post('/platform/forgot-password', { email })
      return data.message
    },

    async resetPassword(token, newPassword) {
      const { data } = await api.post('/platform/reset-password', {
        token,
        new_password: newPassword,
      })
      return data.message
    },

    logout() {
      this.token = null
      this.admin = null
      clearSession()
    },

    async tenants() {
      const { data } = await api.get('/platform/tenants')
      return data.data || []
    },

    async tenant(id) {
      const { data } = await api.get(`/platform/tenants/${id}`)
      return data
    },

    async tenantStats(id) {
      const { data } = await api.get(`/platform/tenants/${id}/stats`)
      return data // { school, plan, usage, stats, billing }
    },

    async tenantStudents(id, params = {}) {
      const { data } = await api.get(`/platform/tenants/${id}/students`, { params })
      return data // { data: [...], meta: {...}, by_status: {...} }
    },

    async revenue() {
      const { data } = await api.get('/platform/revenue')
      return data
    },

    // action: suspend | activate | extend | plan (+ days / plan_tier / plan_cycle)
    async updateTenant(id, payload) {
      const { data } = await api.put(`/platform/tenants/${id}`, payload)
      return data.school
    },

    async impersonate(id) {
      const { data } = await api.post(`/platform/tenants/${id}/impersonate`, {})
      return data // { token, login_url, role }
    },

    // --- Subscription plans / packages ---
    async plans() {
      const { data } = await api.get('/platform/plans')
      return data // { data: [...plans], features: [{key,label}] }
    },

    async createPlan(payload) {
      const { data } = await api.post('/platform/plans', payload)
      return data.plan
    },

    async updatePlan(id, payload) {
      const { data } = await api.put(`/platform/plans/${id}`, payload)
      return data.plan
    },

    async deletePlan(id) {
      await api.delete(`/platform/plans/${id}`)
    },

    // --- Subscription transactions ---
    async transactions(params = {}) {
      const { data } = await api.get('/platform/transactions', { params })
      return data // { data: [...], summary: {...} }
    },

    // Fetch a payment's attached proof as a blob (carries the platform token),
    // returning an object URL the caller opens in a new tab. Caller revokes it.
    async transactionProof(id) {
      const { data } = await api.get(`/platform/transactions/${id}/proof`, {
        responseType: 'blob',
      })
      return URL.createObjectURL(data)
    },

    // Owner marks a pending bank-transfer subscription payment as received,
    // activating that school's plan. { status, plan_tier, plan_cycle, expires_at }.
    async confirmBankPayment(id) {
      const { data } = await api.post(`/platform/billing/confirm/${id}`, {})
      return data
    },

    // --- Payment-integration settings ---
    async getSettings() {
      const { data } = await api.get('/platform/settings')
      return data.data // { paystack_*, has_secret, credo_*, has_credo_secret, bank_*, bank_transfer_enabled, billing_currency }
    },

    async saveSettings(payload) {
      const { data } = await api.put('/platform/settings', payload)
      return data.data
    },

    // --- Marketing website content ---
    async siteContent() {
      const { data } = await api.get('/platform/site')
      return data.data || {}
    },

    async saveSiteContent(content) {
      const { data } = await api.put('/platform/site', { content })
      return data.data || {}
    },

    // --- Blog & Insights posts ---
    // Public reads (no token needed, but harmless to send one).
    async posts(category) {
      const { data } = await api.get('/platform/posts', {
        params: category ? { category } : {},
      })
      return data.data || []
    },

    async post(slug) {
      const { data } = await api.get(`/platform/posts/${encodeURIComponent(slug)}`)
      return data.data
    },

    // Owner console: all posts incl. drafts.
    async managePosts(category) {
      const { data } = await api.get('/platform/posts/manage', {
        params: category ? { category } : {},
      })
      return data.data || []
    },

    async createPost(payload) {
      const { data } = await api.post('/platform/posts', payload)
      return data.data
    },

    async updatePost(id, payload) {
      const { data } = await api.put(`/platform/posts/${id}`, payload)
      return data.data
    },

    async deletePost(id) {
      await api.delete(`/platform/posts/${id}`)
    },

    async uploadPostCover(file) {
      const form = new FormData()
      form.append('cover', file)
      const { data } = await api.post('/platform/posts/upload', form)
      return data.data // { cover_path, cover_url }
    },

    // --- Post categories (admin-managed) ---
    async postCategories() {
      const { data } = await api.get('/platform/posts/categories')
      return data.data || [] // [{ id, name, slug, post_count }]
    },

    async createPostCategory(name) {
      const { data } = await api.post('/platform/posts/categories', { name })
      return data.data
    },

    async updatePostCategory(id, name) {
      const { data } = await api.put(`/platform/posts/categories/${id}`, { name })
      return data.data
    },

    async deletePostCategory(id) {
      await api.delete(`/platform/posts/categories/${id}`)
    },

    // --- Platform branding (name + logo for apex pre-login screens) ---
    async getBranding() {
      const { data } = await api.get('/platform/branding')
      return data.data || {} // { name, logo }
    },

    async saveBranding(payload) {
      const { data } = await api.put('/platform/branding', payload)
      return data.data || {}
    },

    async uploadLogo(file) {
      const form = new FormData()
      form.append('logo', file)
      const { data } = await api.post('/platform/branding/logo', form)
      return data.data || {}
    },

    async uploadBackground(file) {
      const form = new FormData()
      form.append('background', file)
      const { data } = await api.post('/platform/branding/background', form)
      return data.data || {}
    },

    async clearBackground() {
      const { data } = await api.delete('/platform/branding/background')
      return data.data || {}
    },

    // --- Contact / marketing-site enquiries ---
    async contactMessages(params = {}) {
      const { data } = await api.get('/platform/contact', { params })
      this.unreadMessages = data.unread ?? this.unreadMessages
      return data // { data: [...], meta: {...}, unread }
    },

    // Lightweight fetch of just the unread count, for the sidebar badge.
    async loadUnread() {
      try {
        const { data } = await api.get('/platform/contact', { params: { per_page: 1 } })
        this.unreadMessages = data.unread || 0
      } catch {
        // Non-fatal — leave the badge as-is.
      }
      return this.unreadMessages
    },

    async updateContactMessage(id, status) {
      const { data } = await api.put(`/platform/contact/${id}`, { status })
      return data.data
    },

    async deleteContactMessage(id) {
      await api.delete(`/platform/contact/${id}`)
    },

    // --- Data-deletion requests (global triage across all schools) ---
    async dataDeletionRequests(params = {}) {
      const { data } = await api.get('/platform/data-deletion', { params })
      return data // { data: [...], meta: {...}, pending }
    },

    async updateDataDeletionRequest(id, payload) {
      const { data } = await api.put(`/platform/data-deletion/${id}`, payload)
      return data.data
    },

    // --- Platform-wide database backups ---
    async backups() {
      const { data } = await api.get('/platform/backups')
      return data // { data: [...], config: { auto_enabled, retention } }
    },

    async createBackup() {
      const { data } = await api.post('/platform/backups')
      return data.data
    },

    async downloadBackup(name) {
      const res = await api.get(`/platform/backups/${name}/download`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    },

    async deleteBackup(name) {
      await api.delete(`/platform/backups/${name}`)
    },

    async restoreBackup(name) {
      const { data } = await api.post(`/platform/backups/${name}/restore`, { confirm: 'RESTORE' })
      return data
    },

    async saveBackupConfig(config) {
      const { data } = await api.post('/platform/backups/config', config)
      return data.config
    },

    // --- Platform activity log ---
    async audit(params = {}) {
      const { data } = await api.get('/platform/audit', { params })
      return data // { data: [...], meta: {...} }
    },

    async auditFilters() {
      const { data } = await api.get('/platform/audit/filters')
      return data // { actions, entity_types, admins }
    },
  },
})
